import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import { useNavigate } from 'react-router-dom';
import { createSchema } from '../../schemas';
import validationSchema from './validations';

function ServiceAdministratorClientCreate() {
  const { formatMessage } = useLocalization();
  const createFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const [formErrors, setFormErrors] = useState(null);
  const store = useStore();
  const storeName = 'serviceadministratorclientFormStore';

  const postApiUrl = `${getApiUrl()}/sxcl/v1/service-administrator-clients`;

  const preProcessRequestData = data => {
    const {
      status,
      createdByUserId,
      lastModifiedByUserId,
      underwriterApprovalRequired,
      ...other
    } = data;
    return {
      status: status === 'true',
      underwriterApprovalRequired: underwriterApprovalRequired === 'true',
      ...other,
    };
  };

  const submitCreateForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    store.uiState[storeName].set(requestData);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      store.uiState[storeName].set({});
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICE_ADMINISTRATOR_CLIENT_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'Service Administrator Client was saved successfully',
        }),
      );
      createFormRef.current?.resetAndNavigate(
        `/serviceadministratorclients/edit/${response?.data?.id}`,
      );
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEADMINISTRATORCLIENT_CREATE_FAILURE',
          defaultMessage:
            'Error: Could not save the service administrator client',
        }),
      });
    }
  };

  const formProps = {
    customComponents: FormFields,
    schema: createSchema,
    onSubmit: submitCreateForm,
    ref: createFormRef,
    initialValues: store.uiState[storeName].get(),
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICE_ADMINISTRATOR_CLIENTS',
        defaultMessage: 'Service Administrator Clients',
      }),
      icon: IconLibrary.ServiceAdministratorClients,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/serviceadministratorclients');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_SERVICE_ADMINISTRATOR_CLIENT',
        defaultMessage: 'Create Service Administrator Client',
      }),
      icon: IconLibrary.ServiceAdministratorClient,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'LIST_SERVICE_ADMINISTRATOR_CLIENTS',
        defaultMessage: 'List Service Administrator Clients',
      }),
      Icon: IconLibrary.ServiceAdministratorClients,
      variant: 'outlined',
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/serviceadministratorclients');
      },
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'CREATE_SERVICE_ADMINISTRATOR_CLIENT',
          defaultMessage: 'Create Service Administrator Client',
        })}>
        <Box>
          <CRUDComponent
            errors={formErrors}
            type={CRUD_COMPONENT_TYPE.FORM}
            formProps={formProps}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => createFormRef.current?.submit()}>
                {formatMessage({
                  id: 'SAVE_SERVICE_ADMINISTRATOR_CLIENT',
                  defaultMessage: 'Save Service Administrator Client',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => createFormRef.current?.reset()}>
                {formatMessage({
                  id: 'RESET',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </Box>
          </CRUDComponent>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceAdministratorClientCreate);
